import { Facebook, Instagram, Youtube, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { UserSubscribersService } from "../services/UserSubscribersServices";
import { useNotification } from "../context/NotificationContext";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await UserSubscribersService.signup(email);
      if (response.status === "success") {
        showNotification({ message: response.message, type: "success" });
        setEmail("");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        showNotification({ message: response.message, type: "failed" });
      }
    } catch (error) {
      showNotification({
        message: error.response?.data?.message || "Failed to subscribe. Please try again.",
        type: "failed"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="flex flex-col sm:flex-row gap-2" onSubmit={handleSubmit}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        required
        disabled={loading}
        className="flex-1 bg-white/10 border border-white/20 px-4 py-2.5 sm:py-2 text-white placeholder:text-white/60 focus:outline-none focus:border-white transition-colors duration-200 text-sm"
        style={{
          fontFamily: "Montserrat, sans-serif",
        }}
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-white text-black px-5 sm:px-6 py-2.5 sm:py-2 hover:bg-white/90 transition-colors duration-200 touch-manipulation flex items-center justify-center min-w-[100px]"
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "0.5px",
        }}
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : "SIGN UP"}
      </button>
    </form>
  );
}


export default function JazzFooter() {
  const navigationLinks = [
    { name: "Home", href: "/" },
    { name: "Biography", href: "/biography" },
    { name: "Discography", href: "/discography" },
    { name: "Events", href: "/events" },
    { name: "Images", href: "/gallery/images" },
    { name: "Videos", href: "/gallery/videos" },
    { name: "Shop", href: "/shop" },
    { name: "Press", href: "/press" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <footer className="bg-black text-white py-12 sm:py-16 px-4 sm:px-6 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
          {/* Left - Logo and tagline */}
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun logo" className="w-full h-full object-contain scale-[2.25] transition-transform duration-200 group-hover:scale-[1.5]" />
              </div>
              <span
                className="text-lg sm:text-xl font-normal"
                style={{
                  fontFamily: "Playfair Display, Georgia, serif",
                }}
              >
                Temidayo Balogun
              </span>
            </div>
            <p
              className="text-white text-sm leading-relaxed max-w-[280px]"
              style={{
                fontFamily: "Crimson Pro, Georgia, serif",
                fontStyle: "italic",
              }}
            >
              As an artist and seeker, Temidayo weaves the diverse cultural practices of the Yoruba people into his compositions, grounding his sound in the rhythms and harmonies of West Africa.
            </p>
          </div>

          {/* Center - Navigation links */}
          <div>
            <h3
              className="text-xs sm:text-sm font-semibold mb-4 sm:mb-6 tracking-widest text-white"
              style={{
                fontFamily: "Montserrat, sans-serif",
                letterSpacing: "1.5px",
              }}
            >
              NAVIGATE
            </h3>
            <nav className="grid grid-cols-2 gap-x-6 sm:gap-x-8 gap-y-2 sm:gap-y-3">
              {navigationLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-white/85 hover:text-white transition-colors duration-200 text-sm touch-manipulation py-1 font-medium"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right - Social media and newsletter */}
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h3
                className="text-xs sm:text-sm font-semibold mb-4 sm:mb-6 tracking-widest text-white"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  letterSpacing: "1.5px",
                }}
              >
                CONNECT
              </h3>
              <div className="flex gap-3 sm:gap-4">
                <a
                  href="https://www.facebook.com/temidayobalogunmusic/" target="_blank" rel="noreferrer"
                  className="w-10 h-10 sm:w-11 sm:h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 touch-manipulation"
                  aria-label="Facebook"
                >
                  <Facebook size={17} />
                </a>
                <a
                  href="https://www.instagram.com/temidayo_balogun/" target="_blank" rel="noreferrer"
                  className="w-10 h-10 sm:w-11 sm:h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 touch-manipulation"
                  aria-label="Instagram"
                >
                  <Instagram size={17} />
                </a>
                <a
                  href="https://www.youtube.com/@TemidayoBalogun" target="_blank" rel="noreferrer"
                  className="w-10 h-10 sm:w-11 sm:h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 touch-manipulation"
                  aria-label="YouTube"
                >
                  <Youtube size={17} />
                </a>
                {/* <a
                  href="#"
                  className="w-10 h-10 sm:w-11 sm:h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 touch-manipulation"
                  aria-label="Spotify"
                >
                  <Music size={17} />
                </a> */}
              </div>
            </div>

            {/* Newsletter signup */}
            <div>
              <h3
                className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 tracking-widest text-white"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  letterSpacing: "1.5px",
                }}
              >
                NEWSLETTER
              </h3>
              <NewsletterForm />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-[1px] bg-white/10 mb-6 sm:mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/85 font-medium">
          <p
            style={{ fontFamily: "Montserrat, sans-serif" }}
            className="text-center md:text-left"
          >
            © 2026 TEMB Music. All rights reserved.
          </p>
          <div className="flex gap-4 sm:gap-6">
            <Link
              to="/privacy-policy"
              className="hover:text-white transition-colors duration-200 touch-manipulation py-1"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="hover:text-white transition-colors duration-200 touch-manipulation py-1"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
